import { UserRole } from "constants/userRole";
import { ToastContext } from "context/ToastContext";
import { sizer } from "layout/styles/styled/sizer";
import { DEFAULT_UNDO_WINDOW_DAYS } from "pages/Integrations/utils/hstImports";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { ProgressSpinner } from "primereact/progressspinner";
import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useGetAllAccountsQuery } from "store/queries/account";
import {
  useHstImportMutation,
  useHstCancelMutation,
  IHSTImportResult,
  IHSTImportRowResult,
  IHSTCancelResult,
  IHSTCancelRowResult,
} from "store/queries/integration";
import { userSelector } from "store/slices/user";
import styled, { keyframes } from "styled-components";

type ExpandedSection =
  | "created"
  | "updated"
  | "skipped"
  | "duplicates"
  | "errors"
  | null;

type ExpandedCancelSection =
  | "canceled"
  | "review"
  | "ambiguous"
  | "notFound"
  | "invalid"
  | null;

// Outcomes that a human must act on are surfaced as their own cards.
const CANCEL_ACTION_OUTCOMES = new Set(["REVIEW_REQUIRED", "AMBIGUOUS"]);

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(4)};
  min-width: 500px;
`;

const FileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(2)};
`;

const FileLabel = styled.label`
  font-weight: 600;
  font-size: 14px;
`;

const FileInfo = styled.span`
  font-size: 12px;
  color: var(--text-color-secondary);
`;

const SelectedFileDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${sizer(2)};
  background-color: var(--surface-ground);
  border-radius: 4px;
`;

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(3)};
`;

const ResultsSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: ${sizer(2)};
`;

type SummaryCardVariant = "success" | "error" | "warning" | "info" | "neutral";

const SummaryCard = styled.div<{
  variant?: SummaryCardVariant;
  clickable?: boolean;
  expanded?: boolean;
}>`
  padding: ${sizer(2)};
  border-radius: 4px;
  text-align: center;
  cursor: ${({ clickable }) => (clickable ? "pointer" : "default")};
  transition: all 0.15s ease;
  border: 2px solid transparent;
  background-color: ${({ variant }) => {
    switch (variant) {
      case "success":
        return "var(--green-50)";
      case "error":
        return "var(--red-50)";
      case "warning":
        return "var(--yellow-50)";
      default:
        return "var(--surface-ground)";
    }
  }};

  ${({ clickable }) =>
    clickable &&
    `
    &:hover {
      filter: brightness(0.95);
    }
  `}

  ${({ expanded, variant }) => {
    if (!expanded) return "";
    const borderColors: Record<string, string> = {
      success: "var(--green-400)",
      error: "var(--red-400)",
      warning: "var(--yellow-400)",
    };
    return `border-color: ${
      (variant && borderColors[variant]) || "var(--primary-400)"
    };`;
  }}

  .count {
    font-size: 24px;
    font-weight: bold;
    color: ${({ variant }) => {
      switch (variant) {
        case "success":
          return "var(--green-600)";
        case "error":
          return "var(--red-600)";
        case "warning":
          return "var(--yellow-600)";
        default:
          return "var(--text-color)";
      }
    }};
  }

  .label {
    font-size: 12px;
    color: var(--text-color-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  .expand-icon {
    font-size: 10px;
  }
`;

const DetailItem = styled.div`
  padding: ${sizer(2)};
  border-bottom: 1px solid var(--surface-border);
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${sizer(2)};

  &:last-child {
    border-bottom: none;
  }

  .patient-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .patient-name {
    font-weight: 600;
  }

  .dos {
    color: var(--text-color-secondary);
    font-size: 12px;
  }

  .badge {
    flex-shrink: 0;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
  }

  .badge-hst {
    background-color: var(--blue-100);
    color: var(--blue-700);
  }

  .badge-manual {
    background-color: var(--purple-100);
    color: var(--purple-700);
  }

  .error-message {
    color: var(--red-600);
    font-size: 12px;
    max-width: 300px;
    word-break: break-word;
  }
`;

const DetailList = styled.div`
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid var(--surface-border);
  border-radius: 4px;
  background-color: var(--surface-ground);
`;

const DetailSection = styled.div`
  margin-top: ${sizer(2)};
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${sizer(1)};

  .section-title {
    font-weight: 600;
    font-size: 13px;
    color: var(--text-color-secondary);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${sizer(8)};
  gap: ${sizer(2)};
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${sizer(2)};
  padding: ${sizer(3)};
  background-color: var(--blue-50);
  border: 1px solid var(--blue-200);
  border-radius: 4px;
  font-size: 13px;
  color: var(--blue-700);

  .pi-info-circle {
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

// Imports have gone into the wrong account more than once (CLE-2867), so the target account is
// shown in the dialog header, next to the title, in "dragon fire" — a red→orange→gold gradient
// with a slow shimmer — and must be confirmed before Import enables. Ani asked for sparkle,
// rainbows and confetti; the shimmer is the tasteful reading of that. Reduced-motion users get
// the gradient without the animation.
const dragonFireShimmer = keyframes`
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
`;

const DialogHeader = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: ${sizer(1)} ${sizer(4)};
`;

const DragonFireAccountName = styled.span`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.2px;
  background: linear-gradient(
    90deg,
    #b91c1c 0%,
    #ea580c 25%,
    #f59e0b 50%,
    #ea580c 75%,
    #b91c1c 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: #ea580c;
  animation: ${dragonFireShimmer} 3s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const AccountConfirm = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizer(2)};
  font-weight: 600;
`;

const ConflictBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${sizer(2)};
  padding: ${sizer(3)};
  background-color: var(--red-50);
  border: 1px solid var(--red-400);
  border-radius: 4px;
  font-size: 14px;
  color: var(--red-800);
  white-space: pre-wrap;

  .pi {
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

interface IHSTImportDialogProps {
  visible: boolean;
  onHide: () => void;
  accountId: string;
  onSuccess?: () => void;
}

interface ICategorizedCancelResults {
  canceled: IHSTCancelRowResult[];
  review: IHSTCancelRowResult[];
  ambiguous: IHSTCancelRowResult[];
  notFound: IHSTCancelRowResult[];
  invalid: IHSTCancelRowResult[];
}

const CANCEL_SECTION_TITLES: Record<
  Exclude<ExpandedCancelSection, null>,
  string
> = {
  canceled: "Cancelled",
  review: "Needs Review (has a payment)",
  ambiguous: "Ambiguous (multiple matches)",
  notFound: "Not Found",
  invalid: "Unreadable Rows",
};

// Renders the cancellation half of the results view. Kept as a standalone
// function so the create/update import view above is untouched.
function renderCancelResults(
  cancelResult: IHSTCancelResult,
  categorized: ICategorizedCancelResults,
  expandedCancelSection: ExpandedCancelSection,
  toggleCancelSection: (section: ExpandedCancelSection) => void
) {
  const cards: Array<{
    key: Exclude<ExpandedCancelSection, null>;
    label: string;
    count: number;
    rows: IHSTCancelRowResult[];
    variant: SummaryCardVariant;
  }> = [
    {
      key: "canceled",
      label: "Cancelled",
      count: cancelResult.canceledCount + cancelResult.alreadyCanceledCount,
      rows: categorized.canceled,
      variant: "success",
    },
    {
      key: "review",
      label: "Review",
      count: cancelResult.reviewRequiredCount,
      rows: categorized.review,
      variant: "warning",
    },
    {
      key: "ambiguous",
      label: "Ambiguous",
      count: cancelResult.ambiguousCount,
      rows: categorized.ambiguous,
      variant: "warning",
    },
    {
      key: "notFound",
      label: "Not Found",
      count: cancelResult.notFoundCount,
      rows: categorized.notFound,
      variant: "neutral",
    },
    {
      key: "invalid",
      label: "Invalid",
      count: cancelResult.invalidRowCount,
      rows: categorized.invalid,
      variant: "error",
    },
  ];

  const expandedCard = cards.find((card) => card.key === expandedCancelSection);
  const truncated =
    cancelResult.reportedTotal !== null &&
    cancelResult.reportedTotal !== cancelResult.totalRows;

  return (
    <ResultsContainer>
      <DetailHeader>
        <span className="section-title">Cancellations</span>
      </DetailHeader>

      <ResultsSummary>
        <SummaryCard variant="info">
          <div className="count">{cancelResult.totalRows}</div>
          <div className="label">Rows</div>
        </SummaryCard>
        {cards.map((card) => {
          const clickable = card.rows.length > 0;
          return (
            <SummaryCard
              key={card.key}
              variant={card.variant}
              clickable={clickable}
              expanded={expandedCancelSection === card.key}
              onClick={() => clickable && toggleCancelSection(card.key)}
            >
              <div className="count">{card.count}</div>
              <div className="label">
                {card.label}
                {clickable && (
                  <i
                    className={`pi pi-chevron-${
                      expandedCancelSection === card.key ? "up" : "down"
                    } expand-icon`}
                  />
                )}
              </div>
            </SummaryCard>
          );
        })}
      </ResultsSummary>

      {expandedCard && expandedCard.rows.length > 0 && (
        <DetailSection>
          <DetailHeader>
            <span className="section-title">
              {CANCEL_SECTION_TITLES[expandedCard.key]} (
              {expandedCard.rows.length})
            </span>
          </DetailHeader>
          <DetailList>
            {expandedCard.rows.map((row, index) => {
              const actionable = CANCEL_ACTION_OUTCOMES.has(row.outcome);
              // REVIEW_REQUIRED has a single actionable order (orderId); AMBIGUOUS
              // has no single orderId and lists its live matches (matchedOrderIds).
              const orderIds = row.orderId
                ? [row.orderId]
                : row.matchedOrderIds || [];

              return (
                <DetailItem key={index}>
                  <div className="patient-info">
                    <span className="patient-name">{row.patientName}</span>
                    <span className="dos">DOS: {row.srgyDate}</span>
                    {row.message && (
                      <span className="error-message">{row.message}</span>
                    )}
                    {actionable && orderIds.length > 0 && (
                      <span className="dos">
                        {orderIds.map((id, i) => (
                          <a
                            key={id}
                            href={`/orders/${id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ marginRight: 8 }}
                          >
                            Open order {i + 1}
                          </a>
                        ))}
                      </span>
                    )}
                  </div>
                  {row.isNoShow && <span className="badge">No Show</span>}
                </DetailItem>
              );
            })}
          </DetailList>
        </DetailSection>
      )}

      {cancelResult.noShowCount > 0 && (
        <FileInfo>
          {cancelResult.noShowCount} of these rows were no-shows (cancelled the
          same as any other reason).
        </FileInfo>
      )}

      {truncated && (
        <FileInfo>
          Note: the report footer lists {cancelResult.reportedTotal}{" "}
          cancellations but {cancelResult.totalRows} rows were read — the file
          may be truncated.
        </FileInfo>
      )}
    </ResultsContainer>
  );
}

export function HSTImportDialog({
  visible,
  onHide,
  accountId,
  onSuccess,
}: IHSTImportDialogProps) {
  const { current: toastElement } = useContext(ToastContext);
  const [scheduledAppointmentsFile, setScheduledAppointmentsFile] =
    useState<File | null>(null);
  const [insuranceInfoFile, setInsuranceInfoFile] = useState<File | null>(null);
  const [cancelledAppointmentsFile, setCancelledAppointmentsFile] =
    useState<File | null>(null);
  const [importResult, setImportResult] = useState<IHSTImportResult | null>(
    null
  );
  // The undo window is set by the API (HST_IMPORT_UNDO_WINDOW_DAYS) and echoed on the result.
  const undoWindowDays =
    importResult?.undoWindowDays ?? DEFAULT_UNDO_WINDOW_DAYS;
  const [cancelResult, setCancelResult] = useState<IHSTCancelResult | null>(
    null
  );
  const [expandedSection, setExpandedSection] = useState<ExpandedSection>(null);
  const [expandedCancelSection, setExpandedCancelSection] =
    useState<ExpandedCancelSection>(null);

  const [accountConfirmed, setAccountConfirmed] = useState(false);
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);

  const { scope } = useSelector(userSelector);
  const { data: accounts } = useGetAllAccountsQuery(null);
  const accountName = useMemo(
    () => accounts?.data.find((account) => account.id === accountId)?.name,
    [accounts, accountId]
  );

  // A confirmation is for one account only.
  useEffect(() => {
    setAccountConfirmed(false);
    setConflictMessage(null);
  }, [accountId]);

  const [hstImport, { isLoading: isImporting }] = useHstImportMutation();
  const [hstCancel, { isLoading: isCanceling }] = useHstCancelMutation();
  const isLoading = isImporting || isCanceling;

  // Filter results into categories
  const categorizedResults = useMemo(() => {
    if (!importResult?.results) {
      return {
        created: [],
        updated: [],
        skipped: [],
        duplicates: [],
        errors: [],
      };
    }

    const created: IHSTImportRowResult[] = [];
    const updated: IHSTImportRowResult[] = [];
    const skipped: IHSTImportRowResult[] = [];
    const duplicates: Array<
      IHSTImportRowResult & { duplicateType: "hst" | "manual" }
    > = [];
    const errors: IHSTImportRowResult[] = [];

    importResult.results.forEach((row) => {
      if (row.success && row.updated) {
        updated.push(row);
      } else if (row.success && !row.updated) {
        created.push(row);
      } else if (
        row.skipped &&
        (row.errors?.PAYER_EXCLUDED || row.errors?.SECONDARY_PAYER_EXCLUDED)
      ) {
        skipped.push(row);
      } else if (row.skipped && row.errors?.ALREADY_CURRENT) {
        duplicates.push({ ...row, duplicateType: "hst" });
      } else if (row.skipped && row.errors?.DUPLICATE) {
        duplicates.push({ ...row, duplicateType: "manual" });
      } else if (!row.success && row.errors) {
        errors.push(row);
      }
    });

    return { created, updated, skipped, duplicates, errors };
  }, [importResult?.results]);

  // Group cancellation rows by the action a human would take on them.
  const categorizedCancelResults = useMemo(() => {
    const empty = {
      canceled: [] as IHSTCancelRowResult[],
      review: [] as IHSTCancelRowResult[],
      ambiguous: [] as IHSTCancelRowResult[],
      notFound: [] as IHSTCancelRowResult[],
      invalid: [] as IHSTCancelRowResult[],
    };

    if (!cancelResult?.results) {
      return empty;
    }

    cancelResult.results.forEach((row) => {
      switch (row.outcome) {
        case "CANCELED":
        case "ALREADY_CANCELED":
          empty.canceled.push(row);
          break;
        case "REVIEW_REQUIRED":
          empty.review.push(row);
          break;
        case "AMBIGUOUS":
          empty.ambiguous.push(row);
          break;
        case "NOT_FOUND":
          empty.notFound.push(row);
          break;
        case "INVALID_ROW":
          empty.invalid.push(row);
          break;
        default:
          break;
      }
    });

    return empty;
  }, [cancelResult?.results]);

  const handleScheduledAppointmentsSelect = (e: { files: File[] }) => {
    if (e.files && e.files.length > 0) {
      setScheduledAppointmentsFile(e.files[0]);
    }
  };

  const handleInsuranceInfoSelect = (e: { files: File[] }) => {
    if (e.files && e.files.length > 0) {
      setInsuranceInfoFile(e.files[0]);
    }
  };

  const handleCancelledAppointmentsSelect = (e: { files: File[] }) => {
    if (e.files && e.files.length > 0) {
      setCancelledAppointmentsFile(e.files[0]);
    }
  };

  // The scheduled + insurance pair drives the create/update import; the cancelled
  // report drives cancellations. Either half can run on its own, and when both are
  // provided the import runs first so a same-day re-book exists before we look for
  // cancellations.
  const runImport = async (): Promise<boolean> => {
    if (!scheduledAppointmentsFile || !insuranceInfoFile) {
      return true;
    }

    const result = await hstImport({
      accountId,
      scheduledAppointments: scheduledAppointmentsFile,
      insuranceInfo: insuranceInfoFile,
    }).unwrap();

    setImportResult(result.data);

    if (result.data.successCount > 0 || result.data.updateCount > 0) {
      const parts = [];
      if (result.data.successCount > 0) {
        parts.push(`created ${result.data.successCount}`);
      }
      if (result.data.updateCount > 0) {
        parts.push(`updated ${result.data.updateCount}`);
      }
      toastElement?.show({
        severity: "success",
        summary: "Import Complete",
        detail: `Successfully ${parts.join(" and ")} estimates`,
      });
      onSuccess?.();
    }

    if (result.data.errorCount > 0) {
      toastElement?.show({
        severity: "warn",
        summary: "Import Completed with Errors",
        detail: `${result.data.errorCount} rows had errors`,
      });
    }

    return true;
  };

  const runCancellations = async (): Promise<boolean> => {
    if (!cancelledAppointmentsFile) {
      return true;
    }

    const result = await hstCancel({
      accountId,
      cancelledAppointments: cancelledAppointmentsFile,
    }).unwrap();

    setCancelResult(result.data);

    if (result.data.canceledCount > 0) {
      toastElement?.show({
        severity: "success",
        summary: "Cancellations Complete",
        detail: `Cancelled ${result.data.canceledCount} estimates`,
      });
      onSuccess?.();
    }

    const needsAttention =
      result.data.reviewRequiredCount + result.data.ambiguousCount;
    if (needsAttention > 0) {
      toastElement?.show({
        severity: "warn",
        summary: "Some cancellations need review",
        detail: `${needsAttention} rows could not be cancelled automatically`,
      });
    }

    return true;
  };

  const handleImport = async () => {
    const hasImportPair = scheduledAppointmentsFile && insuranceInfoFile;
    const hasScheduledOnly = scheduledAppointmentsFile && !insuranceInfoFile;
    const hasInsuranceOnly = insuranceInfoFile && !scheduledAppointmentsFile;

    if (hasScheduledOnly || hasInsuranceOnly) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          "Scheduled Appointments and Insurance Verification must be uploaded together",
      });
      return;
    }

    if (!hasImportPair && !cancelledAppointmentsFile) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Select the scheduling files and/or a cancellations file",
      });
      return;
    }

    setConflictMessage(null);
    try {
      await runImport();
      await runCancellations();
    } catch (error: any) {
      // 409: this scheduled-appointments file was already imported into another account.
      // The message names that account, so it is shown in the dialog, not only in a toast.
      if (error?.status === 409) {
        setConflictMessage(
          error.data?.message ||
            "This file was already imported into a different account."
        );
      }
      toastElement?.show({
        severity: "error",
        summary: "Import Failed",
        detail: error.data?.message || "An error occurred during import",
      });
    }
  };

  const handleClose = () => {
    setScheduledAppointmentsFile(null);
    setInsuranceInfoFile(null);
    setCancelledAppointmentsFile(null);
    setImportResult(null);
    setCancelResult(null);
    setExpandedSection(null);
    setExpandedCancelSection(null);
    setAccountConfirmed(false);
    setConflictMessage(null);
    onHide();
  };

  const toggleSection = (section: ExpandedSection) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  const toggleCancelSection = (section: ExpandedCancelSection) => {
    setExpandedCancelSection((prev) => (prev === section ? null : section));
  };

  const hasResult = Boolean(importResult || cancelResult);

  const canImport =
    ((scheduledAppointmentsFile && insuranceInfoFile) ||
      cancelledAppointmentsFile) &&
    Boolean(accountName) &&
    accountConfirmed &&
    !isLoading;

  const footerContent = (
    <div className="flex justify-content-end gap-2">
      <Button
        label={hasResult ? "Close" : "Cancel"}
        onClick={handleClose}
        className="p-button-text"
      />
      {!hasResult && (
        <Button
          label="Import"
          onClick={handleImport}
          disabled={!canImport}
          loading={isLoading}
        />
      )}
    </div>
  );

  const renderDialogContent = () => {
    if (isLoading) {
      return (
        <LoadingContainer>
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
          <span>
            {isCanceling && !isImporting
              ? "Processing cancellations... This may take a few moments."
              : "Importing estimates... This may take a few moments."}
          </span>
        </LoadingContainer>
      );
    }

    if (hasResult) {
      const hasCreated = categorizedResults.created.length > 0;
      const hasUpdated = categorizedResults.updated.length > 0;
      const hasSkipped = categorizedResults.skipped.length > 0;
      const hasDuplicates = categorizedResults.duplicates.length > 0;
      const hasErrors = categorizedResults.errors.length > 0;

      return (
        <ResultsContainer>
          {importResult && (
            <ResultsContainer>
              <ResultsSummary>
                <SummaryCard variant="info">
                  <div className="count">{importResult.totalRows}</div>
                  <div className="label">Records</div>
                </SummaryCard>
                <SummaryCard
                  variant="success"
                  clickable={hasCreated}
                  expanded={expandedSection === "created"}
                  onClick={() => hasCreated && toggleSection("created")}
                >
                  <div className="count">{importResult.successCount}</div>
                  <div className="label">
                    Created
                    {hasCreated && (
                      <i
                        className={`pi pi-chevron-${
                          expandedSection === "created" ? "up" : "down"
                        } expand-icon`}
                      />
                    )}
                  </div>
                </SummaryCard>
                <SummaryCard
                  variant="success"
                  clickable={hasUpdated}
                  expanded={expandedSection === "updated"}
                  onClick={() => hasUpdated && toggleSection("updated")}
                >
                  <div className="count">{importResult.updateCount}</div>
                  <div className="label">
                    Updated
                    {hasUpdated && (
                      <i
                        className={`pi pi-chevron-${
                          expandedSection === "updated" ? "up" : "down"
                        } expand-icon`}
                      />
                    )}
                  </div>
                </SummaryCard>
                <SummaryCard
                  variant="neutral"
                  clickable={hasSkipped}
                  expanded={expandedSection === "skipped"}
                  onClick={() => hasSkipped && toggleSection("skipped")}
                >
                  <div className="count">{importResult.skippedPayerCount}</div>
                  <div className="label">
                    Skipped
                    {hasSkipped && (
                      <i
                        className={`pi pi-chevron-${
                          expandedSection === "skipped" ? "up" : "down"
                        } expand-icon`}
                      />
                    )}
                  </div>
                </SummaryCard>
                <SummaryCard
                  variant="warning"
                  clickable={hasDuplicates}
                  expanded={expandedSection === "duplicates"}
                  onClick={() => hasDuplicates && toggleSection("duplicates")}
                >
                  <div className="count">{importResult.duplicateCount}</div>
                  <div className="label">
                    Duplicates
                    {hasDuplicates && (
                      <i
                        className={`pi pi-chevron-${
                          expandedSection === "duplicates" ? "up" : "down"
                        } expand-icon`}
                      />
                    )}
                  </div>
                </SummaryCard>
                <SummaryCard
                  variant="error"
                  clickable={hasErrors}
                  expanded={expandedSection === "errors"}
                  onClick={() => hasErrors && toggleSection("errors")}
                >
                  <div className="count">{importResult.errorCount}</div>
                  <div className="label">
                    Errors
                    {hasErrors && (
                      <i
                        className={`pi pi-chevron-${
                          expandedSection === "errors" ? "up" : "down"
                        } expand-icon`}
                      />
                    )}
                  </div>
                </SummaryCard>
              </ResultsSummary>

              {/* Expanded Detail Section */}
              {expandedSection === "created" && hasCreated && (
                <DetailSection>
                  <DetailHeader>
                    <span className="section-title">
                      Created Orders ({categorizedResults.created.length})
                    </span>
                  </DetailHeader>
                  <DetailList>
                    {categorizedResults.created.map((row, index) => (
                      <DetailItem key={index}>
                        <div className="patient-info">
                          <span className="patient-name">
                            {row.patientName}
                          </span>
                          <span className="dos">DOS: {row.dos}</span>
                        </div>
                      </DetailItem>
                    ))}
                  </DetailList>
                </DetailSection>
              )}

              {expandedSection === "updated" && hasUpdated && (
                <DetailSection>
                  <DetailHeader>
                    <span className="section-title">
                      Updated Orders ({categorizedResults.updated.length})
                    </span>
                  </DetailHeader>
                  <DetailList>
                    {categorizedResults.updated.map((row, index) => (
                      <DetailItem key={index}>
                        <div className="patient-info">
                          <span className="patient-name">
                            {row.patientName}
                          </span>
                          <span className="dos">DOS: {row.dos}</span>
                        </div>
                      </DetailItem>
                    ))}
                  </DetailList>
                </DetailSection>
              )}

              {expandedSection === "skipped" && hasSkipped && (
                <DetailSection>
                  <DetailHeader>
                    <span className="section-title">
                      Skipped - Excluded Payer (
                      {categorizedResults.skipped.length})
                    </span>
                  </DetailHeader>
                  <DetailList>
                    {categorizedResults.skipped.map((row, index) => (
                      <DetailItem key={index}>
                        <div className="patient-info">
                          <span className="patient-name">
                            {row.patientName}
                          </span>
                          <span className="dos">DOS: {row.dos}</span>
                        </div>
                      </DetailItem>
                    ))}
                  </DetailList>
                </DetailSection>
              )}

              {expandedSection === "duplicates" && hasDuplicates && (
                <DetailSection>
                  <DetailHeader>
                    <span className="section-title">
                      Duplicate Orders ({categorizedResults.duplicates.length})
                    </span>
                  </DetailHeader>
                  <DetailList>
                    {categorizedResults.duplicates.map((row, index) => (
                      <DetailItem key={index}>
                        <div className="patient-info">
                          <span className="patient-name">
                            {row.patientName}
                          </span>
                          <span className="dos">DOS: {row.dos}</span>
                        </div>
                        <span
                          className={`badge ${
                            row.duplicateType === "hst"
                              ? "badge-hst"
                              : "badge-manual"
                          }`}
                        >
                          {row.duplicateType === "hst"
                            ? "HST Import"
                            : "Manual Estimate"}
                        </span>
                      </DetailItem>
                    ))}
                  </DetailList>
                </DetailSection>
              )}

              {expandedSection === "errors" && hasErrors && (
                <DetailSection>
                  <DetailHeader>
                    <span className="section-title">
                      Errors ({categorizedResults.errors.length})
                    </span>
                  </DetailHeader>
                  <DetailList>
                    {categorizedResults.errors.map((row, index) => (
                      <DetailItem key={index}>
                        <div className="patient-info">
                          <span className="patient-name">
                            {row.patientName}
                          </span>
                          <span className="dos">DOS: {row.dos}</span>
                          <span className="error-message">
                            {Object.entries(row.errors || {})
                              .map(([key, value]) => value)
                              .join("; ")}
                          </span>
                        </div>
                      </DetailItem>
                    ))}
                  </DetailList>
                </DetailSection>
              )}

              {(importResult.successCount > 0 ||
                importResult.updateCount > 0) && (
                <InfoBox>
                  <i className="pi pi-info-circle" />
                  <span>
                    Eligibility checks are running in the background. Estimates
                    will show updated benefit information once processing is
                    complete.
                  </span>
                </InfoBox>
              )}

              {(importResult.successCount > 0 ||
                importResult.updateCount > 0) && (
                <FileInfo data-testid="hst-import-undo-note">
                  {scope === UserRole.SUPER_ADMIN
                    ? `This import can be undone from Integrations → HST Imports for ${undoWindowDays} days.`
                    : `If these files were for a different account, contact ClearHealth support within ${undoWindowDays} days to undo this import.`}
                </FileInfo>
              )}

              {importResult.noInsuranceMatchCount > 0 && (
                <FileInfo>
                  Note: {importResult.noInsuranceMatchCount} rows did not have
                  matching insurance info.
                </FileInfo>
              )}
            </ResultsContainer>
          )}

          {cancelResult &&
            renderCancelResults(
              cancelResult,
              categorizedCancelResults,
              expandedCancelSection,
              toggleCancelSection
            )}
        </ResultsContainer>
      );
    }

    return (
      <DialogContent>
        {conflictMessage && (
          <ConflictBox role="alert" data-testid="hst-import-conflict">
            <i className="pi pi-exclamation-triangle" />
            <span>{conflictMessage}</span>
          </ConflictBox>
        )}

        <FileSection>
          <FileLabel>Scheduled Appointments CSV</FileLabel>
          <FileInfo>
            Upload the DE5501 - Scheduled Appointments report from HST
          </FileInfo>
          {scheduledAppointmentsFile ? (
            <SelectedFileDisplay>
              <span>{scheduledAppointmentsFile.name}</span>
              <Button
                icon="pi pi-times"
                className="p-button-text p-button-sm"
                onClick={() => setScheduledAppointmentsFile(null)}
              />
            </SelectedFileDisplay>
          ) : (
            <FileUpload
              mode="basic"
              accept=".csv"
              maxFileSize={10000000}
              customUpload
              auto
              uploadHandler={handleScheduledAppointmentsSelect}
              chooseLabel="Select File"
            />
          )}
        </FileSection>

        <FileSection>
          <FileLabel>Insurance Verification CSV</FileLabel>
          <FileInfo>
            Upload the OP5017 - Insurance Verification Datasheet from HST
          </FileInfo>
          {insuranceInfoFile ? (
            <SelectedFileDisplay>
              <span>{insuranceInfoFile.name}</span>
              <Button
                icon="pi pi-times"
                className="p-button-text p-button-sm"
                onClick={() => setInsuranceInfoFile(null)}
              />
            </SelectedFileDisplay>
          ) : (
            <FileUpload
              mode="basic"
              accept=".csv"
              maxFileSize={10000000}
              customUpload
              auto
              uploadHandler={handleInsuranceInfoSelect}
              chooseLabel="Select File"
            />
          )}
        </FileSection>

        <FileSection>
          <FileLabel>Cancelled Appointments CSV (optional)</FileLabel>
          <FileInfo>
            Upload the SC1001 - Cancelled Appointments report to cancel
            estimates for appointments that were called off. Can be uploaded on
            its own.
          </FileInfo>
          {cancelledAppointmentsFile ? (
            <SelectedFileDisplay>
              <span>{cancelledAppointmentsFile.name}</span>
              <Button
                icon="pi pi-times"
                className="p-button-text p-button-sm"
                onClick={() => setCancelledAppointmentsFile(null)}
              />
            </SelectedFileDisplay>
          ) : (
            <FileUpload
              mode="basic"
              accept=".csv"
              maxFileSize={10000000}
              customUpload
              auto
              uploadHandler={handleCancelledAppointmentsSelect}
              chooseLabel="Select File"
            />
          )}
        </FileSection>

        <AccountConfirm>
          <Checkbox
            inputId="hst-import-confirm-account"
            checked={accountConfirmed}
            onChange={(e) => setAccountConfirmed(Boolean(e.checked))}
            disabled={!accountName}
          />
          <label htmlFor="hst-import-confirm-account">
            These files are for {accountName || "this account"}
          </label>
        </AccountConfirm>
      </DialogContent>
    );
  };

  return (
    <Dialog
      header={
        <DialogHeader data-testid="hst-import-account-banner">
          <span>Import Estimates from HST</span>
          <DragonFireAccountName
            title={`Importing into ${accountName || "…"}`}
            aria-label={`Importing into ${accountName || "loading account"}`}
          >
            {accountName || "Loading account..."}
          </DragonFireAccountName>
        </DialogHeader>
      }
      visible={visible}
      onHide={handleClose}
      footer={footerContent}
      style={{ width: hasResult ? "700px" : "auto" }}
    >
      {renderDialogContent()}
    </Dialog>
  );
}
